import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { deleteNit, listNits, type Nit } from "@/api/nits";
import { Input } from "@/components/ui/Field";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DataTable } from "@/components/ui/DataTable";
import { PageHeader } from "@/components/ui/PageHeader";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useConfirmDelete } from "@/components/ui/useConfirmDelete";
import { NitRow } from "@/components/features/nits/NitRow";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { VerNitModal } from "@/components/features/nits/VerNitModal";

const NITS_HEADERS = [
  "NIT",
  "DV",
  "Razón social",
  "Teléfono",
  "Correo",
  "Ciudad",
  "País",
  "Acciones",
] as const;

export const NitsPage = () => {
  const [page, setPage] = useState(1);
  const [viewingNit, setViewingNit] = useState<Nit | null>(null);
  const { value: search, setValue: setSearch, debounced: debouncedSearch } =
    useDebouncedSearch("", 400);
  const limit = 50;

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["nits", page, debouncedSearch],
    queryFn: () => listNits({ page, limit, q: debouncedSearch }),
  });

  const items = data?.data ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { open, close, description, isLoading: deleting, confirm, pending } =
    useConfirmDelete<Nit>({
      queryKey: ["nits", page, debouncedSearch],
      mutationFn: (nit) => deleteNit(nit.nit),
      entityLabel: "NIT",
      successMessage: "NIT eliminado",
      formatDescription: (nit) =>
        `"${nit.razon_social}" (${nit.nit})`,
      onSuccess: () => {
        void refetch();
      },
    });

  return (
    <div className="space-y-4">
      <PageHeader
        title="NITs"
        subtitle="Directorio de terceros (clientes, proveedores) con NIT y DV"
      />

      <Input
        placeholder="Buscar por NIT, razón social, correo..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      <DataTable<Nit>
        headers={NITS_HEADERS}
        data={items}
        isLoading={isLoading || isFetching}
        emptyMessage="No se encontraron NITs."
        rowKey={(n) => n.nit}
        skeletonRows={10}
        renderRow={(n) => (
          <NitRow nit={n} onView={setViewingNit} onDelete={open} />
        )}
      />

      <VerNitModal nit={viewingNit} onClose={() => setViewingNit(null)} />

      {pagination ? (
        <PaginationBar
          page={page}
          totalPages={pagination.total_pages}
          onPageChange={setPage}
          itemCount={items.length}
          totalCount={pagination.total}
          itemLabel="NITs"
        />
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title="¿Eliminar NIT?"
        description={description}
        confirmLabel="Sí, eliminar"
        cancelLabel="Cancelar"
        destructive
        loading={deleting}
        onConfirm={confirm}
        onCancel={close}
      />
    </div>
  );
};